import React, { useState, useEffect } from 'react';
import { Button, Row, Col, Form, FormControl, Modal, FormLabel, Spinner } from 'react-bootstrap';
import { modelContentUtils } from '../utils/helpers';
import useArray from '../hooks/useArray';
import LoadingComponent from '../presenters/LoadingComponent';
import ReportFragment from '../presenters/ReportFragment';
import { PRACTICE_AREA_RELATIONSHIPS } from '../model/practicearearelationships';
import { PRACTICES } from '../model/practices';
import { Constants, getDefaultAPIHeaders, getNodeAPIPath } from '../utils/helpers';
import { post, put, get} from 'aws-amplify/api';

async function fetchData(setDataLoading, appraisals, handleAppraisalChanged_FROM, handleAppraisalChanged_TO) {
    try {
        const raw = await getDefaultAPIHeaders();

        // If raw is { headers: {...} }, unwrap it; otherwise use raw as-is
        const flat = raw && typeof raw.headers === 'object' ? raw.headers : raw;

        // Build a pure string→string map (and drop null/undefined)
        const apiHeaders = Object.fromEntries(
        Object.entries(flat ?? {})
            .filter(([, v]) => v != null)
            .map(([k, v]) => [String(k), String(v)])
        );     
        
        const getOperation = get({ apiName: Constants.API_PATH, 
                                    path: Constants.APPRAISAL_PATH,
                                    options: { headers: apiHeaders },
                                });  
        const response = await getOperation.response;
        const data = await response.body.json(); 
        const formattedAppraisals = data.map(a => {
            return {
                id: a.appraisal_id,
                name: a.appraisal_name,
                view: a.appraisal_view
            }
        });
        if (formattedAppraisals.length > 0) {
            appraisals.setValue(formattedAppraisals);
        }
        else {
            console.log('No appraisals were found.');
        }
    } 
    catch (e) {
        console.log(e);
    }

    setDataLoading(false);
}


const CopyAppraisalDataPracticeModal = (props) => {
    const [dataLoading, setDataLoading] = useState(true);
    const appraisals = useArray([]);

    //from
    const [selectedAppraisalID_FROM, setSelectedAppraisalID_FROM] = useState("false");
    const [selectedProjectID_FROM, setSelectedProjectID_FROM] = useState("false");
    const [selectedPracticeAreaID_FROM, setSelectedPracticeArea_FROM] = useState("false");
    const [selectedPracticeID_FROM, setSelectedPractice_FROM] = useState("false");
    const projects_FROM = useArray([]);
    const practiceAreas_FROM = useArray([]);
    const practices_FROM = useArray([]);

    //to
    const [selectedAppraisalID_TO, setSelectedAppraisalID_TO] = useState("false");
    const [selectedProjectID_TO, setSelectedProjectID_TO] = useState("false");
    const [selectedPracticeAreaID_TO, setSelectedPracticeArea_TO] = useState("false");
    const [selectedPracticeID_TO, setSelectedPractice_TO] = useState("false");
    const projects_TO = useArray([]);
    const practiceAreas_TO = useArray([]);
    const practices_TO = useArray([]);

    //states
    //0 = awaiting appraisal selection
    //1 = awaiting project selection
    //2 = awaiting PA selection
    //3 = awaiting Practice selection
    const [modalOptionsState_FROM, setModalOptionsState_FROM] = useState(0);
    const [modalOptionsState_TO, setModalOptionsState_TO] = useState(0);

    //progress modal
    const [showCopyingModal, setShowCopyingModal] = useState(false);
    const [areItemsCopying, setAreItemsCopying] = useState(false);
    const [itemCopyType, setItemCopyType] = useState("");
    const [itemCopyStatus, setItemCopyStatus] = useState("");

    const defaultSelectionOption = " -- select an option -- ";

    useEffect(() => {
        if (props.showModal) {

            setModalOptionsState_FROM(0);
            setModalOptionsState_TO(0);

            projects_FROM.setValue([]);
            practiceAreas_FROM.setValue([]);
            practices_FROM.setValue([]);

            setSelectedAppraisalID_TO('');
            setSelectedProjectID_TO('');
            setSelectedPracticeArea_TO('');
            setSelectedPractice_TO('');
            
            setDataLoading(true);
            const getData = () => fetchData(setDataLoading, appraisals, handleAppraisalChanged_FROM, handleAppraisalChanged_TO);
            getData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.showModal]);

    useEffect(() => {
        if(selectedAppraisalID_FROM !== ""){
            setModalOptionsState_FROM(1);
        }
    }, [selectedAppraisalID_FROM]);

    useEffect(() => {
        if(selectedProjectID_FROM !== ""){
            setModalOptionsState_FROM(2);
        }
    }, [selectedProjectID_FROM]);

    useEffect(() => {
        if(selectedPracticeAreaID_FROM !== ""){
            setModalOptionsState_FROM(3);
        }
    }, [selectedPracticeAreaID_FROM]);
    
    useEffect(() => {
        if(selectedAppraisalID_TO !== ""){
            setModalOptionsState_TO(1);
        }
    }, [selectedAppraisalID_TO]);

    useEffect(() => {
        if(selectedProjectID_TO !== ""){
            setModalOptionsState_TO(2);
        }
    }, [selectedProjectID_TO]);

    useEffect(() => {
        if(selectedPracticeAreaID_TO !== ""){
            setModalOptionsState_TO(3);
        }
    }, [selectedPracticeAreaID_TO]);

    const handleAppraisalChanged_FROM = async (selectedAppraisal) => {

        //reset previous states
        setModalOptionsState_FROM(0);
        projects_FROM.setValue([]);
        practiceAreas_FROM.setValue([]);
        practices_FROM.setValue([]);
        setSelectedProjectID_FROM('');
        setSelectedPracticeArea_FROM('');
        setSelectedPractice_FROM('');

        if(selectedAppraisal === ""){
            return;
        }

        try {
            const raw = await getDefaultAPIHeaders();

            // If raw is { headers: {...} }, unwrap it; otherwise use raw as-is
            const flat = raw && typeof raw.headers === 'object' ? raw.headers : raw;

            // Build a pure string→string map (and drop null/undefined)
            const apiHeaders = Object.fromEntries(
            Object.entries(flat ?? {})
                .filter(([, v]) => v != null)
                .map(([k, v]) => [String(k), String(v)])
            );     
            
            const getOperation = get({ apiName: Constants.API_PATH, 
                                        path:  `${Constants.APPRAISAL_PATH}/${selectedAppraisal}`,
                                        options: { headers: apiHeaders },
                                    });  
            const response = await getOperation.response;
            const currentAppraisalData = await response.body.json(); 
            const formattedProjectData = currentAppraisalData.projects
                .filter(x => x.project_name !== "Organizational Roll-up")
                .map(p => {
                    return {
                        id: p.project_id,
                        name: p.project_name,
                        practiceAreas: p.practice_areas
                    }
            });

            //set new states
            setSelectedAppraisalID_FROM(selectedAppraisal);
            projects_FROM.setValue(formattedProjectData);
        }
        catch (e) {
            console.log(e);
        }
    }

    const handleProjectChanged_FROM = async (projectID) => {

        //reset previous states
        setModalOptionsState_FROM(1);
        practiceAreas_FROM.setValue([]);
        practices_FROM.setValue([]);
        setSelectedPracticeArea_FROM('');
        setSelectedPractice_FROM('');

        if(projectID === ""){
            return;
        }

        let selectedProject = projects_FROM.value.find(p => p.id === projectID);
        if(selectedProject){
            
            //set new states
            setSelectedProjectID_FROM(projectID);
            practiceAreas_FROM.setValue(selectedProject.practiceAreas);
        }
    }

    const handlePracticeAreaChanged_FROM = async (practiceArea) => {

        //reset previous states
        setModalOptionsState_FROM(2);
        practices_FROM.setValue([]);
        setSelectedPractice_FROM('');

        if(practiceArea === ""){
            return;
        }

        setSelectedPracticeArea_FROM(practiceArea);
        let practiceSet = [];
        let nodes = PRACTICE_AREA_RELATIONSHIPS[practiceArea];
        if(nodes){
            for(let i = 0; i < nodes.length; i++){
                practiceSet.push(PRACTICES[nodes[i].Id]);
            }
        }
        
        //set new states
        practices_FROM.setValue(practiceSet);
    }

    const handlePracticeChanged_FROM = (practice) => {

        //reset previous states
        setModalOptionsState_FROM(3);

        if(practice === ""){
            setSelectedPractice_FROM('');
            return;
        }

        //set new states
        setSelectedPractice_FROM(practice);
    }

    const getAppraisalOptionsList_FROM = () => {
        let options = [];
        options.push(<option selected value="">{defaultSelectionOption}</option>);
        for(let i = 0; i < appraisals.value.length; i++){
            options.push(<option value={appraisals.value[i].id}>{appraisals.value[i].name}</option>)
        }
        return (
            <React.Fragment>
                {options}
            </React.Fragment>
        )
    }

    const getProjectOptionsList_FROM = () => {
        let options = [];
        options.push(<option selected value="">{defaultSelectionOption}</option>);
        if(modalOptionsState_FROM >= 1){
            for(let i = 0; i < projects_FROM.value.length; i++){
                options.push(<option value={projects_FROM.value[i].id}>{projects_FROM.value[i].name}</option>)
            }
        }
        return (
            <React.Fragment>
                {options}
            </React.Fragment>
        )
    }

    const getPracticeAreasOptionsList_FROM = () => {
        let options = [];
        options.push(<option selected value="">{defaultSelectionOption}</option>);
        if(modalOptionsState_FROM >= 2){
            for(let i = 0; i < practiceAreas_FROM.value.length; i++){
                options.push(<option value={practiceAreas_FROM.value[i]}>{practiceAreas_FROM.value[i]}</option>)
            }
        }
        return (
            <React.Fragment>
                {options}
            </React.Fragment>
        )
    }

    const getPracticeOptionsList_FROM = () => {
        let options = [];
        options.push(<option selected value="">{defaultSelectionOption}</option>);
        if(modalOptionsState_FROM >= 3){
            for(let i = 0; i < practices_FROM.value.length; i++){
                options.push(<option value={practices_FROM.value[i].Id}>{practices_FROM.value[i].Abbreviation}</option>)
            }
        }
        return (
            <React.Fragment>
                {options}
            </React.Fragment>
        )
    }

    const handleAppraisalChanged_TO = async (selectedAppraisal) => {

        //reset previous states
        setModalOptionsState_TO(0);
        projects_TO.setValue([]);
        practiceAreas_TO.setValue([]);
        practices_TO.setValue([]);
        setSelectedProjectID_TO('');
        setSelectedPracticeArea_TO('');
        setSelectedPractice_TO('');

        if(selectedAppraisal === ""){
            return;
        }

        try {
            const raw = await getDefaultAPIHeaders();

            // If raw is { headers: {...} }, unwrap it; otherwise use raw as-is
            const flat = raw && typeof raw.headers === 'object' ? raw.headers : raw;

            // Build a pure string→string map (and drop null/undefined)
            const apiHeaders = Object.fromEntries(
            Object.entries(flat ?? {})
                .filter(([, v]) => v != null)
                .map(([k, v]) => [String(k), String(v)])
            );     
            
            const getOperation = get({ apiName: Constants.API_PATH, 
                                        path: `${Constants.APPRAISAL_PATH}/${selectedAppraisal}`,
                                        options: { headers: apiHeaders },
                                    });  
            const response = await getOperation.response;                                    
            const currentAppraisalData = await response.body.json(); 
            const formattedProjectData = currentAppraisalData.projects
                .filter(x => x.project_name !== "Organizational Roll-up")
                .map(p => {
                    return {
                        id: p.project_id,
                        name: p.project_name,
                        practiceAreas: p.practice_areas
                    }
            });

            //set current states
            setSelectedAppraisalID_TO(selectedAppraisal);
            projects_TO.setValue(formattedProjectData);
        }
        catch (e) {
            console.log(e);
        }
    }

    const handleProjectChanged_TO = async (projectID) => {

        //reset previous states
        setModalOptionsState_TO(1);
        practiceAreas_TO.setValue([]);
        practices_TO.setValue([]);
        setSelectedPracticeArea_TO('');
        setSelectedPractice_TO('');

        if(projectID === ""){
            return;
        }

        let selectedProject = projects_TO.value.find(p => p.id === projectID);
        if(selectedProject){

            //set current states
            setSelectedProjectID_TO(projectID);
            practiceAreas_TO.setValue(selectedProject.practiceAreas);
        }
    }

    const handlePracticeAreaChanged_TO = async (practiceArea) => {

        //reset previous states
        setModalOptionsState_TO(2);
        practices_TO.setValue([]);
        setSelectedPractice_TO('');

        if(practiceArea === ""){
            return;
        }

        setSelectedPracticeArea_TO(practiceArea);
        let practiceSet = [];
        let nodes = PRACTICE_AREA_RELATIONSHIPS[practiceArea];
        if(nodes){
            for(let i = 0; i < nodes.length; i++){
                practiceSet.push(PRACTICES[nodes[i].Id]);
            }
        }
        
        //set current states
        practices_TO.setValue(practiceSet);
    }

    const handlePracticeChanged_TO = (practice) => {

        //reset previous states
        setModalOptionsState_TO(3);

        if(practice === ""){
            setSelectedPractice_TO("");
            return;
        }

        //set current states
        setSelectedPractice_TO(practice);
    }

    const getAppraisalOptionsList_TO = () => {
        let options = [];
        options.push(<option selected value="">{defaultSelectionOption}</option>);
        for(let i = 0; i < appraisals.value.length; i++){
            options.push(<option value={appraisals.value[i].id}>{appraisals.value[i].name}</option>)
        }
        return (
            <React.Fragment>
                {options}
            </React.Fragment>
        )
    }

    const getProjectOptionsList_TO = () => {
        let options = [];
        options.push(<option selected value="">{defaultSelectionOption}</option>);
        if(modalOptionsState_TO >= 1){
            for(let i = 0; i < projects_TO.value.length; i++){
                options.push(<option value={projects_TO.value[i].id}>{projects_TO.value[i].name}</option>)
            }
        }
        return (
            <React.Fragment>
                {options}
            </React.Fragment>
        )
    }

    const getPracticeAreasOptionsList_TO = () => {
        let options = [];
        options.push(<option selected value="">{defaultSelectionOption}</option>);
        if(modalOptionsState_TO >= 2){
            for(let i = 0; i < practiceAreas_TO.value.length; i++){
                options.push(<option value={practiceAreas_TO.value[i]}>{practiceAreas_TO.value[i]}</option>)
            }
        }
        return (
            <React.Fragment>
                {options}
            </React.Fragment>
        )
    }

    const getPracticeOptionsList_TO = () => {
        let options = [];
        options.push(<option selected value="">{defaultSelectionOption}</option>);
        if(modalOptionsState_TO >= 3){
            for(let i = 0; i < practices_TO.value.length; i++){
                options.push(<option value={practices_TO.value[i].Id}>{practices_TO.value[i].Abbreviation}</option>)
            }
        }
        return (
            <React.Fragment>
                {options}
            </React.Fragment>
        )
    }

    const executeClick = async () => {

        setShowCopyingModal(true);
        setAreItemsCopying(true);

        if(props.options.Evidence){
            await copyEvidence();
        }
        
        if(props.options.Interviews){
            await copyInterviews();
        }

        if(props.options.Notes 
        || props.options.Strengths
        || props.options.Weaknesses
        || props.options.IOs){
            await copyFindings();
        }

        setItemCopyType("Copy Complete.");
        setAreItemsCopying(false);
    }

    const copyEvidence = async () => {

        setItemCopyType("Copying Evidence...");

        //get the FROM evidence
        const nodeInfo = {
            appraisalId: selectedAppraisalID_FROM,
            projectId: selectedProjectID_FROM,
            nodeId: selectedPracticeID_FROM
        }
        const raw = await getDefaultAPIHeaders();

        // If raw is { headers: {...} }, unwrap it; otherwise use raw as-is
        const flat = raw && typeof raw.headers === 'object' ? raw.headers : raw;

        // Build a pure string→string map (and drop null/undefined)
        const apiHeaders = Object.fromEntries(
        Object.entries(flat ?? {})
            .filter(([, v]) => v != null)
            .map(([k, v]) => [String(k), String(v)])
        );     
        
        const getOperation = get({ apiName: Constants.API_PATH, 
                                    path: getNodeAPIPath(nodeInfo, Constants.APPRAISAL_EVIDENCE_PATH),
                                    options: { headers: apiHeaders },
                             });  
        const response = await getOperation.response;
        const evidenceData = await response.body.json(); 

        let body;
        let postOperation;                     
        for(let i = 0; i < evidenceData.length; i++){

            //create the TO evidence for every FROM evidence
            body = {
                appraisal_id: selectedAppraisalID_TO,
                project_id: selectedProjectID_TO,
                node_id: selectedPracticeID_TO,
                evidence_fileName: evidenceData[i].evidence_fileName,
                evidence_path: evidenceData[i].evidence_path,
                evidence_notes: evidenceData[i].evidence_notes,
                evidence_state: evidenceData[i].evidence_state,
                isHyperlink: evidenceData[i].isHyperlink,
            };

            try {      
                postOperation = post({ apiName: Constants.API_PATH, 
                        path: Constants.APPRAISAL_EVIDENCE_PATH,
                        options: { headers: apiHeaders, body },
                        });
                await postOperation.response;  
            }
            catch (e) {
                console.log('evidence post failure' + e);
            }
        }
        setItemCopyStatus("Evidence Copied Successfully");
    }

    const copyInterviews = async () => {

        setItemCopyType("Copying Interviews...");

        //get the FROM interviews
        const nodeInfo = {
            appraisalId: selectedAppraisalID_FROM,
            projectId: selectedProjectID_FROM,
            nodeId: selectedPracticeID_FROM
        }
        const raw = await getDefaultAPIHeaders();

        // If raw is { headers: {...} }, unwrap it; otherwise use raw as-is
        const flat = raw && typeof raw.headers === 'object' ? raw.headers : raw;

        // Build a pure string→string map (and drop null/undefined)
        const apiHeaders = Object.fromEntries(
        Object.entries(flat ?? {})
            .filter(([, v]) => v != null)
            .map(([k, v]) => [String(k), String(v)])
        );     
        
        const getOperation = get({ apiName: Constants.API_PATH, 
                                    path: getNodeAPIPath(nodeInfo, Constants.INTERVIEW_QUESTIONS_PATH),
                                    options: { headers: apiHeaders },
                                });  
        const response = await getOperation.response;
        const interviewData = await response.body.json(); 

        let body;
        let postOperation;

        for(let i = 0; i < interviewData.length; i++){

            //create the TO interviews for every FROM interview
            body = {
                appraisal_id: selectedAppraisalID_TO,
                project_id: selectedProjectID_TO,
                node_id: selectedPracticeID_TO,
                question: interviewData[i].question,
                sessions: interviewData[i].sessions
            }
            try {
                postOperation = post({ apiName: Constants.API_PATH, 
                        path: Constants.INTERVIEW_QUESTIONS_PATH,
                        options: { headers: apiHeaders, body },
                        });
                await postOperation.response;  
            }
            catch (e) {
                console.log("add interview question failure" + e);
            }
        }
        setItemCopyStatus("Interviews Copied Successfully");
    }

    const copyFindings = async () => {
        setItemCopyType("Copying Findings...");

        //get the FROM findings
        const nodeInfo = {
            appraisalId: selectedAppraisalID_FROM,
            projectId: selectedProjectID_FROM,
            nodeId: selectedPracticeID_FROM
        }
        const raw = await getDefaultAPIHeaders();

        // If raw is { headers: {...} }, unwrap it; otherwise use raw as-is
        const flat = raw && typeof raw.headers === 'object' ? raw.headers : raw;

        // Build a pure string→string map (and drop null/undefined)
        const apiHeaders = Object.fromEntries(
        Object.entries(flat ?? {})
            .filter(([, v]) => v != null)
            .map(([k, v]) => [String(k), String(v)])
        );     
        
        const getOperations = get({ apiName: Constants.API_PATH, 
                                    path: getNodeAPIPath(nodeInfo, Constants.APPRAISAL_ITEM_PATH),
                                    options: { headers: apiHeaders },
                                });  
        const response = await getOperations.response;
        const findingsData = await response.body.json(); 

        let body;
        let putOperation;

        for(let i = 0; i < findingsData.length; i++){

            //filter any findings whose options are not chosen
            if((findingsData[i].item_type === "notes" && !props.options.Notes)
            || (findingsData[i].item_type === "strengths" && !props.options.Strengths)
            || (findingsData[i].item_type === "weaknesses" && !props.options.Weaknesses)
            || (findingsData[i].item_type === "improvement_opportunities" && !props.options.IOs)) {
                continue;
            }
            
            body = {
                appraisal_id: selectedAppraisalID_TO,
                project_id: selectedProjectID_TO,
                node_id: selectedPracticeID_TO,
                item_type: findingsData[i].item_type,
                item_data: findingsData[i].item_data,
                revision_number: 0
            }
            try {
                putOperation = put({ apiName: Constants.API_PATH, 
                        path: Constants.APPRAISAL_ITEM_PATH,
                        options: { headers: apiHeaders, body },
                        });  
                await putOperation.response;
            }
            catch (e) {
                console.log("add findings failure" + e);
            }  
        }
         setItemCopyStatus("Findings Copied Successfully");
    }

    const getCopyingButtonText = () => {
        return !areItemsCopying ? "Close" : " Copying Appraisal Data, please wait...";
    }

    const getSelectedOptions = () => {
        let selectedOptions = [];
        if(props.options.Evidence){
            selectedOptions.push("Evidence");
        }
        if(props.options.Interviews){
            selectedOptions.push("Interviews");
        }
        if(props.options.Notes){
            selectedOptions.push("Notes");
        }
        if(props.options.Strengths){
            selectedOptions.push("Strengths");
        }
        if(props.options.Weaknesses){
            selectedOptions.push("Weaknesses");
        }
        if(props.options.IOs){
            selectedOptions.push("Improvement Opportunities");
        }
        return selectedOptions.join(', ');
    }

    const tryCloseModal = () => {
        if(!areItemsCopying){
            props.setShowModal(false);
        }
    }

    const tryCloseCopyingModal = () => {
        if (!areItemsCopying) {
            setShowCopyingModal(false);
            props.setShowModal(false);
        }
    }

    return (
        <React.Fragment>
            <Modal show={props.showModal} onHide={(e) => tryCloseModal()} dialogClassName="findingsReportModalStyle">
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: '18px', color: 'dimgray' }}>
                        <img
                            src={require('../assets/icon.png')}
                            width="30"
                            height="30"
                            className="d-inline-block align-middle"
                            alt="CAP logo"
                        />{' '}Copy Appraisal Data by Practice</Modal.Title>
                </Modal.Header>
                <LoadingComponent isLoading={dataLoading} iconSize={80}>
                    <ReportFragment >
                        <Row className="d-flex justify-content-center" style={{ marginTop: '-50px' }}>
                       
                        <Form.Label
                            column md='2'
                            style={{
                                color: 'white',
                                backgroundColor: 'rgb(0,168,168)',
                                fontSize: '16px',
                                textAlign: 'center',
                                padding: '.5rem'
                            }}>
                            Selected Options
                        </Form.Label>
                        
                        <Col md={5}>
                            {getSelectedOptions()}
                        </Col>
                        </Row>

                        <Row className="d-flex justify-content-center" style={{ marginTop: '50px' }}>
                            <Col md={5}>
                                <Form>
                                    <Form.Group as={Row} className="d-flex justify-content-center">
                                        <FormLabel
                                            style={{
                                                color: 'rgb(0,168,168)',
                                                fontSize: '20px',
                                                textAlign: 'right',
                                                fontWeight: 'bold'
                                            }}>
                                            Copy From
                                        </FormLabel>
                                    </Form.Group>
                                    <Form.Group as={Row} className="d-flex justify-content-center">
                                        <Form.Label
                                            column sm='3'
                                            style={{
                                                color: 'white',
                                                backgroundColor: 'rgb(0,168,168)',
                                                fontSize: '16px',
                                                textAlign: 'center',
                                                padding: '.5rem'
                                            }}>
                                            Appraisal
                                        </Form.Label>
                                        <Col sm={6} style={{ textAlign: 'left' }}>
                                            <FormControl
                                                as="select"
                                                style={{ borderRightColor: 'white', borderTopColor: 'white' }}
                                                selected={selectedAppraisalID_FROM}
                                                onChange={(e) => handleAppraisalChanged_FROM(e.target.value)}>
                                                {getAppraisalOptionsList_FROM()}
                                            </FormControl>
                                        </Col>
                                    </Form.Group>
                                    <Form.Group as={Row} className="d-flex justify-content-center">
                                        <Form.Label
                                            column sm='3'
                                            style={{
                                                color: 'white',
                                                backgroundColor: 'rgb(0,168,168)',
                                                fontSize: '16px',
                                                textAlign: 'center',
                                                padding: '.5rem'
                                            }}>
                                            Project
                                        </Form.Label>
                                        <Col sm={6} style={{ textAlign: 'left' }}>
                                            <FormControl id="projectsSelect_FROM"
                                                as="select"
                                                style={{ borderRightColor: 'white', borderTopColor: 'white' }}
                                                disabled={modalOptionsState_FROM < 1}
                                                selected={selectedProjectID_FROM}
                                                onChange={(e) => handleProjectChanged_FROM(e.target.value)}>
                                                {getProjectOptionsList_FROM()}
                                            </FormControl>
                                        </Col>
                                    </Form.Group>
                                    <Form.Group as={Row} className="d-flex justify-content-center">
                                        <Form.Label
                                            column sm='3'
                                            style={{
                                                color: 'white',
                                                backgroundColor: 'rgb(0,168,168)',
                                                fontSize: '16px',
                                                textAlign: 'center',
                                                padding: '.5rem'
                                            }}>
                                            Practice Area
                                        </Form.Label>
                                        <Col sm={6} style={{ textAlign: 'left' }}>
                                            <FormControl id="practiceAreaSelect_FROM"
                                                as="select"
                                                style={{ borderRightColor: 'white', borderTopColor: 'white' }}
                                                disabled={modalOptionsState_FROM < 2}
                                                selected={selectedPracticeAreaID_FROM}
                                                onChange={(e) => handlePracticeAreaChanged_FROM(e.target.value)}>
                                                {getPracticeAreasOptionsList_FROM()}
                                            </FormControl>
                                        </Col>
                                    </Form.Group>
                                    <Form.Group as={Row} className="d-flex justify-content-center">
                                        <Form.Label
                                            column sm='3'
                                            style={{
                                                color: 'white',
                                                backgroundColor: 'rgb(0,168,168)',
                                                fontSize: '16px',
                                                textAlign: 'center',
                                                padding: '.5rem'
                                            }}>
                                            Practice
                                        </Form.Label>
                                        <Col sm={6} style={{ textAlign: 'left' }}>
                                            <FormControl id="practiceSelect_FROM"
                                                as="select"
                                                style={{ borderRightColor: 'white', borderTopColor: 'white' }}
                                                disabled={modalOptionsState_FROM < 3}
                                                selected={selectedPracticeID_FROM}
                                                onChange={(e) => handlePracticeChanged_FROM(e.target.value)}>
                                                {getPracticeOptionsList_FROM()}
                                            </FormControl>
                                        </Col>
                                    </Form.Group>
                                </Form>
                            </Col>



                            <Col xs={1} style={{maxWidth:"5px", background: "rgb(0,168,168)", paddingLeft:"1px", paddingRight:"1px"}}>
                            </Col> 

                            

                            <Col md={5}>
                            <Form>
                                    <Form.Group as={Row} className="d-flex justify-content-center">
                                        <FormLabel
                                            style={{
                                                color: 'rgb(0,168,168)',
                                                fontSize: '20px',
                                                textAlign: 'right',
                                                fontWeight: 'bold'
                                            }}>
                                            Copy To
                                        </FormLabel>
                                    </Form.Group>
                                    <Form.Group as={Row} className="d-flex justify-content-center">
                                        <Form.Label
                                            column sm='3'
                                            style={{
                                                color: 'white',
                                                backgroundColor: 'rgb(0,168,168)',
                                                fontSize: '16px',
                                                textAlign: 'center',
                                                padding: '.5rem'
                                            }}>
                                            Appraisal
                                        </Form.Label>
                                        <Col sm={6} style={{ textAlign: 'left' }}>
                                            <FormControl
                                                as="select"
                                                style={{ borderRightColor: 'white', borderTopColor: 'white' }}
                                                selected={selectedAppraisalID_TO}
                                                onChange={(e) => handleAppraisalChanged_TO(e.target.value)}>
                                                {getAppraisalOptionsList_TO()}
                                            </FormControl>
                                        </Col>
                                    </Form.Group>
                                    <Form.Group as={Row} className="d-flex justify-content-center">
                                        <Form.Label
                                            column sm='3'
                                            style={{
                                                color: 'white',
                                                backgroundColor: 'rgb(0,168,168)',
                                                fontSize: '16px',
                                                textAlign: 'center',
                                                padding: '.5rem'
                                            }}>
                                            Project
                                        </Form.Label>
                                        <Col sm={6} style={{ textAlign: 'left' }}>
                                            <FormControl
                                                as="select"
                                                style={{ borderRightColor: 'white', borderTopColor: 'white' }}
                                                disabled={modalOptionsState_TO < 1}
                                                selected={selectedProjectID_TO}
                                                onChange={(e) => handleProjectChanged_TO(e.target.value)}>
                                                {getProjectOptionsList_TO()}
                                            </FormControl>
                                        </Col>
                                    </Form.Group>
                                    <Form.Group as={Row} className="d-flex justify-content-center">
                                        <Form.Label
                                            column sm='3'
                                            style={{
                                                color: 'white',
                                                backgroundColor: 'rgb(0,168,168)',
                                                fontSize: '16px',
                                                textAlign: 'center',
                                                padding: '.5rem'
                                            }}>
                                            Practice Area
                                        </Form.Label>
                                        <Col sm={6} style={{ textAlign: 'left' }}>
                                            <FormControl
                                                as="select"
                                                style={{ borderRightColor: 'white', borderTopColor: 'white' }}
                                                disabled={modalOptionsState_TO < 2}
                                                selected={selectedPracticeAreaID_TO}
                                                onChange={(e) => handlePracticeAreaChanged_TO(e.target.value)}>
                                                {getPracticeAreasOptionsList_TO()}
                                            </FormControl>
                                        </Col>
                                    </Form.Group>
                                    <Form.Group as={Row} className="d-flex justify-content-center">
                                        <Form.Label
                                            column sm='3'
                                            style={{
                                                color: 'white',
                                                backgroundColor: 'rgb(0,168,168)',
                                                fontSize: '16px',
                                                textAlign: 'center',
                                                padding: '.5rem'
                                            }}>
                                            Practice
                                        </Form.Label>
                                        <Col sm={6} style={{ textAlign: 'left' }}>
                                            <FormControl
                                                as="select"
                                                style={{ borderRightColor: 'white', borderTopColor: 'white' }}
                                                disabled={modalOptionsState_TO < 3}
                                                selected={selectedPracticeID_TO}
                                                onChange={(e) => handlePracticeChanged_TO(e.target.value)}>
                                                {getPracticeOptionsList_TO()}
                                            </FormControl>
                                        </Col>
                                    </Form.Group>
                                </Form>
                            </Col>
                        </Row>
                        <Row className="d-flex justify-content-center">
                            <Button
                                variant="outline-light"
                                disabled={selectedPracticeID_FROM === "" || selectedPracticeID_TO === ""}
                                style={{
                                    background: 'rgb(0, 168, 168)',
                                    color: 'white',
                                    border: 'none',
                                    justifyContent: 'center',
                                    padding: '.5rem',
                                    marginTop: '1rem',
                                    marginBottom: '2rem'
                                }}
                                size='sm'
                                onClick={() => executeClick()}
                                >
                                Copy Practice
                            </Button>{' '}
                        </Row>
                    </ReportFragment>
                </LoadingComponent>
            </Modal>

            <Modal show={showCopyingModal} onHide={(e) => tryCloseCopyingModal(e)} >
                <Modal.Header >
                    <Modal.Title style={{ fontSize: '18px', color: 'dimgray' }}>
                        <img
                            src={require('../assets/icon.png')}
                            width="30"
                            height="30"
                            className="d-inline-block align-middle"
                            alt="CAP logo"
                        />Copy Appraisal Data</Modal.Title>
                </Modal.Header>
                <Row className="d-flex justify-content-center">
                    <Col className='col-md-10'>
                        <Modal.Body>
                            <text style={{ fontSize: '16px', color: 'dimgray' }}>{itemCopyType}</text>
                            <br></br>
                            <text style={{ fontSize: '12px', color: 'dimgray' }}>{itemCopyStatus}</text>
                        </Modal.Body>
                    </Col>
                </Row>
                <Modal.Footer style={{ backgroundColor: 'rgb(0,168,168)' }}>
                    <Button
                        variant="outline-light"
                        disabled={areItemsCopying}
                        size='sm'
                        onClick={(e) => tryCloseCopyingModal(e)}>
                        {areItemsCopying &&
                            <Spinner
                                as="span"
                                animation="border"
                                size='sm'
                                role="status"
                                aria-hidden="true"
                            />}
                        {getCopyingButtonText()}
                    </Button>{' '}
                </Modal.Footer>
            </Modal>
        </React.Fragment>
    );
}
export default CopyAppraisalDataPracticeModal;